import React, { useState } from 'react';
import { loginWithGoogle, loginWithEmail, registerWithEmail } from '../services/firebase';

const LoginScreen = ({ onLogin }) => {
  const [loginMode, setLoginMode] = useState('login');
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAuth = async () => {
    if (!authData.email || !authData.password) {
      setError('Preencha email e senha');
      return;
    }

    if (loginMode === 'register' && !authData.displayName) {
      setError('Preencha seu nome');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (loginMode === 'register') {
        await registerWithEmail(authData.email, authData.password, authData.displayName);
      } else {
        await loginWithEmail(authData.email, authData.password);
      }
      // Não precisa chamar onLogin - o useAuth detecta automaticamente
    } catch (error) {
      console.error('Erro:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Este email já está em uso');
      } else if (error.code === 'auth/weak-password') {
        setError('Senha muito fraca. Use pelo menos 6 caracteres');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido');
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Email ou senha incorretos');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Credenciais inválidas. Verifique email e senha');
      } else {
        setError('Erro ao fazer login. Tente novamente');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const user = await loginWithGoogle();
      onLogin(user);
    } catch (error) {
      console.error('Erro:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Login cancelado');
      } else {
        setError('Erro ao fazer login com Google');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="surface-raised w-full max-w-md p-6 sm:p-8">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🎮</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Já Joguei</h1>
          <p className="text-slate-400 text-sm">Organize sua biblioteca de jogos</p>
        </div>

        <div className="flex gap-2 mb-6 bg-slate-800/70 p-1 rounded-lg">
          <button
            onClick={() => { setLoginMode('login'); setError(''); }}
            className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${
              loginMode === 'login' ? 'bg-violet-600 text-white' : 'text-slate-400'
            }`}
          >
            Login
          </button>
          <button
            onClick={() => { setLoginMode('register'); setError(''); }}
            className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${
              loginMode === 'register' ? 'bg-violet-600 text-white' : 'text-slate-400'
            }`}
          >
            Registrar
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {loginMode === 'register' && (
            <input
              type="text"
              placeholder="Nome"
              value={authData.displayName}
              onChange={(e) => setAuthData({ ...authData, displayName: e.target.value })}
              className="input-field py-3"
              disabled={loading}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={authData.email}
            onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
            className="input-field py-3"
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Senha"
            value={authData.password}
            onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
            className="input-field py-3"
            disabled={loading}
            onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
          />
          <button
            onClick={handleAuth}
            disabled={loading}
            className="btn-primary w-full py-3 text-sm"
          >
            {loading ? 'Aguarde...' : (loginMode === 'login' ? 'Entrar' : 'Criar Conta')}
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-800"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-slate-900 text-slate-500">ou</span>
          </div>
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full bg-white text-slate-800 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50 text-sm active:scale-[0.98]"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading ? 'Aguarde...' : 'Continuar com Google'}
        </button>

        {loginMode === 'register' && (
          <p className="text-xs text-slate-500 text-center mt-4">
            Ao criar uma conta, você concorda com nossos termos de uso
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
